/**
 * Whether a failed request is terminal — the server rejected it on its own terms and repeating the
 * identical request cannot produce a different answer.
 *
 * The REST client auto-retries list endpoints on a fixed schedule and is meant to stop early on a
 * 4xx. Its guard reads `error.status`, but the generated client throws a `ResponseError` that
 * carries the status on `error.response.status` instead — so the guard never fires and a
 * deterministic 400 is retried through every interval, roughly nine seconds of waiting for an
 * answer that will not change. This reads BOTH shapes.
 *
 * Callers use it to decide immediately, rather than to suppress a genuine server fault: a 5xx is
 * not terminal and must still be treated as a real failure.
 */
export function clientErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const record = error as { status?: unknown; response?: { status?: unknown } };
  const direct = typeof record.status === 'number' ? record.status : undefined;
  const nested = typeof record.response?.status === 'number' ? record.response.status : undefined;
  return direct ?? nested;
}

/** A 4xx: the request was rejected on its own terms and will be rejected identically again. */
export function isTerminalClientError(error: unknown): boolean {
  const status = clientErrorStatus(error);
  return status !== undefined && status >= 400 && status < 500;
}
